import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  SelectChangeEvent,
  Autocomplete,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { fetchWhitelist, addWhitelist, removeWhitelist, fetchLocations, updateWhitelist } from '../../store/slices/locationSlice';
import { AppDispatch, RootState } from '../../store/store';
import { DropdownItem, LocationWhitelistDto } from '../../types';
import { masterApi } from '../../api/masterApi';
import { toolsApi } from '../../api/toolsApi';

const ALL_EMPLOYEES_VALUE = '__ALL_EMPLOYEES__';
const ALL_LOCATIONS_VALUE = '__ALL_LOCATIONS__';
const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, { label: 'All', value: -1 }];

const LocationWhitelistPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { whitelist, locations, loading } = useSelector((state: RootState) => state.locations);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkLocationMode, setBulkLocationMode] = useState(false);
  const [bulkEmployeeMode, setBulkEmployeeMode] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [remarks, setRemarks] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [employees, setEmployees] = useState<DropdownItem[]>([]);
  const [filterLocationId, setFilterLocationId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
   const [departments, setDepartments] = useState<DropdownItem[]>([]);
    const getFirstDefined = (...values: any[]) => {
      for (const value of values) {
        if (value !== undefined && value !== null) return value;
      }
      return undefined;
    };
  useEffect(() => {
    dispatch(fetchWhitelist({}));
    dispatch(fetchLocations({}));
    void loadEmployees();
  }, [dispatch]);

  const loadEmployees = async () => {
    try {
       masterApi.getDepartments().then(setDepartments).catch(() => {});
      toolsApi.getEmployees().then((result: any[]) => {
        const normalized = (result || []).map((item) => ({
          id: String(getFirstDefined(item?.id, item?.Id, '')),
          name: String(getFirstDefined(item?.name, item?.Name, '')),
          //employeeCode: getFirstDefined(item?.employeeCode, item?.EmployeeCode, undefined ),
        })).filter((item) => item.id && item.name);
        setEmployees(normalized);
      }).catch(() => {});
    } catch {
      setEmployees([]);
    }
  };


  //  useEffect(() => {
  //     masterApi.getDepartments().then(setDepartments).catch(() => {});
  //     toolsApi.getEmployees().then((result: any[]) => {
  //       const normalized = (result || []).map((item) => ({
  //         id: String(getFirstDefined(item?.id, item?.Id, '')),
  //         name: String(getFirstDefined(item?.name, item?.Name, '')),
  //         employeeCode: getFirstDefined(item?.employeeCode, item?.EmployeeCode, undefined),
  //       })).filter((item) => item.id && item.name);
  //       setEmployees(normalized);
  //     }).catch(() => {});
  //   }, []);
  


  const getErrorMessage = (error: unknown, fallback: string) => {
    if (typeof error === 'string' && error.trim()) return error;
    if (error instanceof Error && error.message.trim()) return error.message;
    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    return fallback;
  };

  const resetAddForm = () => {
    setBulkLocationMode(false);
    setBulkEmployeeMode(false);
    setSelectedLocationId('');
    setSelectedLocationIds([]);
    setSelectedEmployeeId('');
    setSelectedEmployeeIds([]);
    setRemarks('');
    setIsActive(true);
    setEditingEntryId(null);
  };

  const handleCloseAdd = () => {
    resetAddForm();
    setDialogOpen(false);
  };

  const handleOpenAdd = () => {
    resetAddForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (entry: LocationWhitelistDto) => {
    setBulkLocationMode(false);
    setBulkEmployeeMode(false);
    setSelectedLocationId(entry.locationId);
    setSelectedLocationIds([]);
    setSelectedEmployeeId(entry.employeeId);
    setSelectedEmployeeIds([]);
    setRemarks(entry.remarks || '');
    setIsActive(entry.isActive);
    setEditingEntryId(entry.id);
    setDialogOpen(true);
  };

  const handleFilterChange = (locationId: string) => {
    setFilterLocationId(locationId);
    setPage(0);
    dispatch(fetchWhitelist({ locationId: locationId || undefined }));
  };

  const activeLocations = locations.filter((location) => location.isActive);
  const selectableLocations = editingEntryId
    ? activeLocations
    : activeLocations;
  const selectedEditLocation = editingEntryId
    ? locations.find((location) => String(location.id) === selectedLocationId)
    : undefined;
  const editLocationOptions = editingEntryId && selectedEditLocation && !selectedEditLocation.isActive
    ? [selectedEditLocation, ...activeLocations.filter((location) => location.id !== selectedEditLocation.id)]
    : activeLocations;

  const allLocationIds = selectableLocations.map((location) => String(location.id));
  const allEmployeeIds = employees.map((employee) => String(employee.id));
  const allLocationsSelected = allLocationIds.length > 0 && selectedLocationIds.length === allLocationIds.length;
  const allEmployeesSelected = allEmployeeIds.length > 0 && selectedEmployeeIds.length === allEmployeeIds.length;
  const selectedLocationIdSet = new Set(selectedLocationIds);
  const selectedEmployeeIdSet = new Set(selectedEmployeeIds);

  const toggleAllLocations = () => {
    setSelectedLocationIds(allLocationsSelected ? [] : allLocationIds);
  };

  const toggleAllEmployees = () => {
    setSelectedEmployeeIds(allEmployeesSelected ? [] : allEmployeeIds);
  };

  const handleLocationMultiChange = (event: SelectChangeEvent<string[]>) => {
    const value = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;

    if (value.includes(ALL_LOCATIONS_VALUE)) {
      toggleAllLocations();
      return;
    }

    setSelectedLocationIds(value);
  };

  const handleEmployeeMultiChange = (event: SelectChangeEvent<string[]>) => {
    const value = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;

    if (value.includes(ALL_EMPLOYEES_VALUE)) {
      toggleAllEmployees();
      return;
    }

    setSelectedEmployeeIds(value);
  };

  const resolvedLocationIds = () => {
    if (!bulkLocationMode) {
      return selectedLocationId ? [selectedLocationId] : [];
    }

    return selectedLocationIds;
  };

  const resolvedEmployeeIds = () => {
    if (!bulkEmployeeMode) {
      return selectedEmployeeId ? [selectedEmployeeId] : [];
    }

    return selectedEmployeeIds;
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      if (editingEntryId) {
        if (!selectedLocationId || !selectedEmployeeId) {
          throw new Error('Please select a location and employee.');
        }

        const duplicateEntryExists = whitelist.some((entry) =>
          entry.id !== editingEntryId &&
          String(entry.locationId) === selectedLocationId &&
          String(entry.employeeId) === selectedEmployeeId
        );

        if (duplicateEntryExists) {
          throw new Error('This employee is already whitelisted for the selected location.');
        }

        await dispatch(updateWhitelist({
          id: editingEntryId,
          data: {
            locationId: selectedLocationId,
            employeeId: selectedEmployeeId,
            remarks: remarks || undefined,
            isActive,
          },
        })).unwrap();

        setSnackbar({
          open: true,
          message: 'Whitelist entry updated successfully.',
          severity: 'success',
        });

        handleCloseAdd();
        dispatch(fetchWhitelist({ locationId: filterLocationId || undefined }));
        return;
      }

      const uniqueLocationIds = Array.from(new Set(resolvedLocationIds())).filter(Boolean);
      const uniqueEmployeeIds = Array.from(new Set(resolvedEmployeeIds())).filter(Boolean);

      if (uniqueLocationIds.length === 0 || uniqueEmployeeIds.length === 0) {
        throw new Error('Please select at least one location and employee.');
      }

      const requests = uniqueLocationIds.flatMap((locationId) =>
        uniqueEmployeeIds.map((employeeId) => ({
          locationId,
          employeeId,
          remarks: remarks || undefined,
        }))
      );

      const newRequests = requests.filter((request) =>
        !whitelist.some((entry) =>
          String(entry.locationId) === request.locationId &&
          String(entry.employeeId) === request.employeeId
        )
      );

      const skippedCount = requests.length - newRequests.length;

      if (newRequests.length === 0) {
        throw new Error('All selected employees are already whitelisted for the selected location(s).');
      }

      const results = await Promise.allSettled(
        newRequests.map((request) => dispatch(addWhitelist(request)).unwrap())
      );

      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failureCount = results.length - successCount;
      const firstFailure = results.find((result) => result.status === 'rejected');

      if (successCount === 0) {
        throw new Error(
          firstFailure?.status === 'rejected'
            ? getErrorMessage(firstFailure.reason, 'Failed to add whitelist entry.')
            : 'Failed to add whitelist entry.'
        );
      }

      setSnackbar({
        open: true,
        message:
          failureCount > 0 && firstFailure?.status === 'rejected'
            ? `${successCount} whitelist entry(s) added, ${failureCount} failed. ${getErrorMessage(firstFailure.reason, '')}`.trim()
            : `${successCount} whitelist entry(s) added successfully.${skippedCount > 0 ? ` ${skippedCount} already existed and were skipped.` : ''}`,
        severity: failureCount > 0 ? 'error' : 'success',
      });

      resetAddForm();
      dispatch(fetchWhitelist({ locationId: filterLocationId || undefined }));
    } catch (error) {
      setSnackbar({
        open: true,
        message: getErrorMessage(error, 'Failed to add whitelist entry.'),
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (id: string) => {
    const result = await Swal.fire({
      title: 'Remove Whitelist Entry',
      text: 'Remove this employee from the location whitelist?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, remove',
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await dispatch(removeWhitelist(id)).unwrap();
      setSnackbar({ open: true, message: 'Whitelist entry removed', severity: 'success' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: getErrorMessage(error, 'Remove failed'),
        severity: 'error',
      });
    }
  };

  const renderMultiValue = (selected: string[], allValue: string, singularLabel: string) => {
    if (selected.length === 0) return `Select ${singularLabel}s`;
    const isAllSelected = allValue === ALL_LOCATIONS_VALUE ? allLocationsSelected : allEmployeesSelected;
    if (isAllSelected) return `All ${singularLabel.charAt(0).toUpperCase()}${singularLabel.slice(1)}s`;
    return `${selected.length} ${singularLabel}(s) selected`;
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredWhitelist = whitelist.filter((entry) => {
    if (!normalizedSearch) return true;

    return [
      entry.employeeName,
      entry.employeeCode,
      entry.locationName,
      entry.remarks,
      entry.isActive ? 'active' : 'inactive',
    ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
  });

  const pagedWhitelist = rowsPerPage === -1
    ? filteredWhitelist
    : filteredWhitelist.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          <SecurityIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Location Whitelisting
        </Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleOpenAdd} sx={{ borderRadius: 2 ,textTransform: 'none' }}>
          Whitelist Employee
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Filter by Location</InputLabel>
            <Select
              value={filterLocationId}
              label="Filter by Location"
              onChange={(event) => handleFilterChange(event.target.value as string)}
            >
              <MenuItem value="">All Locations</MenuItem>
              {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.locationName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Search"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setPage(0);
            }}
            placeholder="Search employee, code, location, remarks"
            sx={{ minWidth: 320 }}
          />
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Whitelisted employees are allowed to punch attendance from the assigned GPS locations.
        Employees not in the whitelist will be denied check-in from those locations.
      </Alert>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Employee</strong></TableCell>
                <TableCell><strong>Employee Code</strong></TableCell>
                <TableCell><strong>Location</strong></TableCell>
                <TableCell><strong>Remarks</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWhitelist.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No whitelist entries found for the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedWhitelist.map((entry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell><Typography fontWeight={500}>{entry.employeeName}</Typography></TableCell>
                    <TableCell><Chip label={entry.employeeCode || '-'} size="small" variant="outlined" /></TableCell>
                    <TableCell>{entry.locationName}</TableCell>
                    <TableCell>{entry.remarks || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={entry.isActive ? 'Active' : 'Inactive'}
                        color={entry.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(entry)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleRemove(entry.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {!loading && filteredWhitelist.length > 0 && (
        <TablePagination
          component={Paper}
          count={filteredWhitelist.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          sx={{ mt: 1, borderRadius: 2 }}
        />
      )}

      <Dialog open={dialogOpen} onClose={handleCloseAdd} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEntryId ? 'Edit Whitelist Entry' : 'Whitelist Employee for Location'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ position: 'relative' }}>
            {submitting && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.65)',
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1, opacity: submitting ? 0.65 : 1 }}>
            <FormControl fullWidth required>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <InputLabel shrink sx={{ position: 'static', transform: 'none' }}>Location</InputLabel>
                {!editingEntryId && (
                  <FormControlLabel
                    control={
                    <Switch
                      checked={bulkLocationMode}
                      onChange={(event) => {
                        const enabled = event.target.checked;
                        setBulkLocationMode(enabled);
                        setSelectedLocationId('');
                        setSelectedLocationIds([]);
                      }}
                      disabled={submitting}
                      size="small"
                    />
                  }
                  label="Bulk Select"
                    sx={{ mr: 0 }}
                  />
                )}
              </Box>
              {bulkLocationMode && !editingEntryId ? (
                <Select
                  multiple
                  value={selectedLocationIds}
                  displayEmpty
                  renderValue={(selected) => renderMultiValue(selected as string[], ALL_LOCATIONS_VALUE, 'location')}
                  onChange={handleLocationMultiChange}
                  disabled={submitting}
                  MenuProps={{ PaperProps: { style: { maxHeight: 320 } }, transitionDuration: 0 }}
                >
                  <MenuItem value={ALL_LOCATIONS_VALUE} dense>
                    <Checkbox
                      checked={allLocationsSelected}
                      indeterminate={!allLocationsSelected && selectedLocationIds.length > 0}
                      disableRipple
                    />
                    <ListItemText primary="All Locations" />
                  </MenuItem>
                  {selectableLocations.map((location) => (
                    <MenuItem key={location.id} value={String(location.id)} dense>
                      <Checkbox checked={selectedLocationIdSet.has(String(location.id))} disableRipple />
                      <ListItemText primary={`${location.locationName} - ${location.city || location.address || 'N/A'}`} />
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Select
                  value={selectedLocationId}
                  label="Location"
                  displayEmpty
                  disabled={submitting}
                  onChange={(event) => setSelectedLocationId(event.target.value as string)}
                >
                  <MenuItem value="">
                    <em>Select location</em>
                  </MenuItem>
                  {editLocationOptions.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.locationName} - {location.city || location.address || 'N/A'}{!location.isActive ? ' (Inactive)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>
            <FormControl fullWidth required>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <InputLabel shrink sx={{ position: 'static', transform: 'none' }}>
                  Employee
                </InputLabel>

                {!editingEntryId && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bulkEmployeeMode}
                        onChange={(event) => {
                          const enabled = event.target.checked;
                          setBulkEmployeeMode(enabled);
                          setSelectedEmployeeId('');
                          setSelectedEmployeeIds([]);
                        }}
                        disabled={submitting}
                        size="small"
                      />
                    }
                    label="Bulk Select"
                    sx={{ mr: 0 }}
                  />
                )}
              </Box>
            {bulkEmployeeMode && !editingEntryId ? (
              // ✅ BULK MODE (Multi Select)
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allEmployeesSelected}
                      indeterminate={selectedEmployeeIds.length > 0 && !allEmployeesSelected}
                      onChange={() => {
                        setSelectedEmployeeIds(allEmployeesSelected ? [] : allEmployeeIds);
                      }}
                      disabled={submitting || employees.length === 0}
                      size="small"
                    />
                  }
                  label="Select All Employees"
                  sx={{ mb: 1 }}
                />
                <Autocomplete
                  multiple
                  size="small"
                  sx={{ minWidth: 260 }}
                  options={employees}
                  disableCloseOnSelect
                  value={employees.filter((emp) =>
                    selectedEmployeeIds.includes(String(emp.id))
                  )}
                  onChange={(_, newValues) => {
                    setSelectedEmployeeIds(newValues.map((v) => String(v.id)));
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) =>
                    option.id
                      ? `${option.name}`
                      : option.name
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} disableRipple />
                      {option.id
                        ? `${option.name}`
                        : option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employees"
                      placeholder="Search and select employees..."
                    />
                  )}
                  disabled={submitting}
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  Already whitelisted employees are skipped automatically when adding.
                </Alert>
              </Box>
            ) : (
              // ✅ SINGLE MODE
              <Autocomplete
                size="small"
                sx={{ minWidth: 260 }}
                options={employees}
                value={
                  employees.find((emp) => String(emp.id) === selectedEmployeeId) || null
                }
                onChange={(_, newValue) =>
                  setSelectedEmployeeId(newValue?.id ? String(newValue.id) : '')
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) =>
                  option.id
                    ? `${option.name}`
                    : option.name
                }
                renderInput={(params) => (
                  <TextField {...params} label="Employee" />
                )}
                disabled={submitting}
              />
            )}
            </FormControl>
            {editingEntryId && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    disabled={submitting}
                    onChange={(event) => setIsActive(event.target.checked)}
                  />
                }
                label={isActive ? 'Active' : 'Inactive'}
              />
            )}

            <TextField
              label="Remarks"
              fullWidth
              multiline
              rows={2}
              value={remarks}
              disabled={submitting}
              onChange={(event) => setRemarks(event.target.value)}
              placeholder="Optional - reason for whitelisting"
            />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdd} disabled={submitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              submitting ||
              (bulkLocationMode ? selectedLocationIds.length === 0 : !selectedLocationId) ||
              (bulkEmployeeMode ? selectedEmployeeIds.length === 0 : !selectedEmployeeId)
            }
            sx={{textTransform:'none'}}
          >
            {submitting
              ? editingEntryId ? 'Updating...' : 'Adding...'
              : editingEntryId ? 'Update Whitelist' : 'Add to Whitelist'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationWhitelistPage;
